import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import { ApiError, apiRequest, clearSession, getRefreshToken } from '@/api/client'

export type IdentityType = 'email' | 'phone'
export type SecurityVerificationAction = 'bind_phone' | 'change_phone' | 'bind_email' | 'change_email' | 'register_passkey' | 'delete_account' | 'change_password'
export type SecurityVerificationMethod = 'phone' | 'email' | 'totp'
export type IdentityVerificationAction = Exclude<SecurityVerificationAction, 'register_passkey' | 'delete_account' | 'change_password'>

export interface CurrentUser {
  userId: string
  userNo: string
  homeRegion?: string
  status: string
  hasPassword: boolean
  phone: string
  email: string
  phoneBound: boolean
  emailBound: boolean
  marketingConsent: boolean
}

export interface TwoFactorStatus {
  enabled: boolean
  pending: boolean
  method?: string
}

export interface TwoFactorSetup {
  secret: string
  otpAuthUri: string
}

export interface PasskeyCredentialInfo {
  id: string
  name: string
  authenticatorAttachment?: string
  transports: string[]
  backupEligible: boolean
  backedUp: boolean
  createdAt: string
  lastUsedAt?: string
}

interface VerificationTokenResponse {
  expiresIn: number
}

interface ChangeIdentityInput {
  securityVerificationToken: string
  targetVerificationToken: string
}

interface PasskeyRegistrationOptionsResponse {
  ceremonyToken: string
  options: {
    mediation?: string
    publicKey: PublicKeyCredentialCreationOptionsJSON
  }
}

const authErrorMessages: Readonly<Record<number, string>> = {
  4001001: '请求参数或验证方式不正确',
  4001101: '请输入有效的验证码',
  4001201: '通行密钥请求参数不正确',
  4001301: '安全验证请求参数不正确',
  4001401: '账号注销请求无效，请重新验证后再试',
  4001601: '新密码不符合要求',
  4011002: '验证码错误或已失效',
  4011101: '二次验证码错误、已使用或已失效',
  4011201: '通行密钥验证失败或已失效',
  4011301: '验证码或安全验证令牌已失效',
  4041201: '通行密钥不存在或已被移除',
  4090001: '该手机号或邮箱已被其他账户使用',
  4091101: '二次验证状态已变化，请刷新后重试',
  4091201: '该通行密钥已注册或状态已变化',
  4091202: '通行密钥数量已达到上限',
  4091301: '当前登录会话无法完成安全验证，请重新登录',
  4091401: '账号状态已变化或已有注销请求',
  4091601: '新密码不能与当前密码相同',
  4290001: '验证码发送过于频繁，请稍后重试',
  4291302: '今日短信次数已达上限，请明天再试',
  4291101: '验证失败次数过多，请稍后重试',
  4291201: '通行密钥验证失败次数过多，请稍后重试',
}

export function accountErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? authErrorMessages[error.code] ?? error.message ?? fallback : fallback
}

function normalizeWebAuthnOptions(options: PublicKeyCredentialCreationOptionsJSON) {
  const normalized = { ...options } as unknown as Record<string, unknown>
  for (const key of ['excludeCredentials', 'hints', 'attestationFormats', 'extensions']) {
    if (normalized[key] === null) {
      delete normalized[key]
    }
  }
  const authenticatorSelection = normalized.authenticatorSelection
  if (authenticatorSelection && typeof authenticatorSelection === 'object') {
    const selection = { ...(authenticatorSelection as Record<string, unknown>) }
    if (!['platform', 'cross-platform'].includes(selection.authenticatorAttachment as string)) {
      delete selection.authenticatorAttachment
    }
    normalized.authenticatorSelection = selection
  }
  return normalized as unknown as PublicKeyCredentialCreationOptionsJSON
}

export const accountApi = {
  me: () => apiRequest<CurrentUser>('/auth/v1/me'),

  getSecurityVerificationMethods: (action: SecurityVerificationAction) => apiRequest<{ methods: SecurityVerificationMethod[] }>(`/user/v1/security-verifications/methods?action=${encodeURIComponent(action)}`),

  sendSecurityVerificationCode(action: SecurityVerificationAction, method: Exclude<SecurityVerificationMethod, 'totp'>) {
    return apiRequest<Record<string, never>>('/user/v1/security-verifications/codes', {
      method: 'POST',
      headers: { 'X-Idempotency-Key': crypto.randomUUID() },
      body: { action, method },
    })
  },

  verifySecurityVerification(action: SecurityVerificationAction, method: SecurityVerificationMethod, code: string) {
    return apiRequest<VerificationTokenResponse & { securityVerificationToken: string }>('/user/v1/security-verifications/verify', {
      method: 'POST',
      body: { action, method, code },
    })
  },

  sendIdentityVerificationCode(action: IdentityVerificationAction, identityType: IdentityType, identifier: string) {
    return apiRequest<Record<string, never>>('/user/v1/identity-verifications/codes', {
      method: 'POST',
      headers: { 'X-Idempotency-Key': crypto.randomUUID() },
      body: { action, identityType, identifier },
    })
  },

  verifyIdentityVerification(action: IdentityVerificationAction, identityType: IdentityType, identifier: string, code: string) {
    return apiRequest<VerificationTokenResponse & { targetVerificationToken: string }>('/user/v1/identity-verifications/verify', {
      method: 'POST',
      body: { action, identityType, identifier, code },
    })
  },

  changePhone: (input: ChangeIdentityInput) => apiRequest<Record<string, never>>('/user/v1/phone', { method: 'PUT', body: input }),
  changeEmail: (input: ChangeIdentityInput) => apiRequest<Record<string, never>>('/user/v1/email', { method: 'PUT', body: input }),
  getMarketingPreference: () => apiRequest<{ enabled: boolean }>('/user/v1/marketing-preference'),
  updateMarketingPreference: (enabled: boolean) => apiRequest<{ enabled: boolean }>('/user/v1/marketing-preference', { method: 'PUT', body: { enabled } }),
  changePassword: (securityVerificationToken: string, newPassword: string) => apiRequest<Record<string, never>>('/user/v1/password', { method: 'PUT', body: { securityVerificationToken, newPassword } }),
  deleteAccount: (securityVerificationToken: string) => apiRequest<{ requestId: string, status: string, requestedAt: string }>('/user/v1/account', { method: 'DELETE', retryAuth: false, body: { securityVerificationToken } }),
  getTwoFactorStatus: () => apiRequest<TwoFactorStatus>('/user/v1/twoFactor'),
  setupTwoFactor: () => apiRequest<TwoFactorSetup>('/user/v1/twoFactor/setup', { method: 'POST' }),
  enableTwoFactor: (code: string) => apiRequest<{ recoveryCodes: string[] }>('/user/v1/twoFactor/verify', { method: 'POST', body: { code } }),
  disableTwoFactor: (code: string) => apiRequest<Record<string, never>>('/user/v1/twoFactor/disable', { method: 'POST', body: { code } }),
  regenerateRecoveryCodes: (code: string) => apiRequest<{ recoveryCodes: string[] }>('/user/v1/twoFactor/recoveryCodes/regenerate', { method: 'POST', body: { code } }),

  async beginPasskeyRegistration(name: string, securityVerificationToken: string) {
    const response = await apiRequest<PasskeyRegistrationOptionsResponse>('/user/v1/passkeys/registration/options', {
      method: 'POST',
      body: { name, securityVerificationToken },
    })
    response.options.publicKey = normalizeWebAuthnOptions(response.options.publicKey)
    return response
  },
  finishPasskeyRegistration: (ceremonyToken: string, credential: RegistrationResponseJSON) => apiRequest<PasskeyCredentialInfo>('/user/v1/passkeys/registration/verify', { method: 'POST', body: { ceremonyToken, credential } }),
  listPasskeys: () => apiRequest<{ items: PasskeyCredentialInfo[] }>('/user/v1/passkeys'),
  renamePasskey: (id: string, name: string) => apiRequest<Record<string, never>>(`/user/v1/passkeys/${encodeURIComponent(id)}`, { method: 'PUT', body: { name } }),
  removePasskey: (id: string) => apiRequest<Record<string, never>>(`/user/v1/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  async logout() {
    const refreshToken = getRefreshToken()
    try {
      if (refreshToken) {
        await apiRequest<Record<string, never>>('/auth/v1/logout', { method: 'POST', retryAuth: false, body: { refreshToken } })
      }
    }
    finally {
      clearSession()
    }
  },
}
